import { useState, useMemo, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ShoppingCart, Star, Check, Search, ChevronDown, X } from 'lucide-react';
import Button from '@components/common/Button';
import { addToCart } from '@store/slices/cartSlice';
import productsData from '@data/products.json';
import notificationService from '@services/api/notification.service';

// Reusable collapsible filter dropdown (click to expand, click outside to close)
const FilterDropdown = ({ label, value, children, isDefault }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
          open ? 'border-[#c33c3d] bg-red-50/40' : 'border-gray-200 bg-white hover:bg-gray-50'
        }`}
      >
        <span className="flex items-baseline gap-1.5 truncate">
          <span className="text-[11px] font-bold text-gray-400 tracking-wide">{label}</span>
          <span className={`truncate ${isDefault ? 'text-gray-500 font-normal' : 'text-gray-900 font-semibold'}`}>
            {value}
          </span>
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1.5 w-full sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg p-3 max-h-64 overflow-y-auto">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
};

const Cart = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector((state) => state.cart.items);
  const [selectedMainCategory, setSelectedMainCategory] = useState('books');
  const [selectedSubcategory, setSelectedSubcategory] = useState('All');
  const [selectedPrice, setSelectedPrice] = useState('All Prices');
  const [searchTerm, setSearchTerm] = useState('');
  const [addedToCart, setAddedToCart] = useState({});

  const mainCategories = productsData.mainCategories;
  const priceRanges = ['All Prices', '₹0 - ₹500', '₹500 - ₹1000', '₹1000+'];

  const currentCategory = mainCategories.find(cat => cat.id === selectedMainCategory);
  const subcategories = ['All', ...(currentCategory?.subcategories || [])];

  const filteredProducts = useMemo(() => {
    let filtered = productsData.products.filter(product => product.category === selectedMainCategory);

    if (selectedSubcategory !== 'All') {
      filtered = filtered.filter(product => product.subcategory === selectedSubcategory);
    }

    if (selectedPrice !== 'All Prices') {
      filtered = filtered.filter(product => {
        if (selectedPrice === '₹0 - ₹500') return product.price <= 500;
        if (selectedPrice === '₹500 - ₹1000') return product.price > 500 && product.price <= 1000;
        if (selectedPrice === '₹1000+') return product.price > 1000;
        return true;
      });
    }

    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        product =>
          product.title.toLowerCase().includes(q) ||
          product.author?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [selectedMainCategory, selectedSubcategory, selectedPrice, searchTerm]);

  const handleAddToCart = async (product) => {
    dispatch(addToCart(product));
    setAddedToCart(prev => ({ ...prev, [product.id]: true }));
    
    try {
      await notificationService.notifyCartAddition(product);
    } catch (error) {
      console.error('Failed to notify admin about cart addition:', error);
    }
    
    setTimeout(() => {
      setAddedToCart(prev => ({ ...prev, [product.id]: false }));
    }, 2000);
  };

  const isInCart = (productId) => {
    return cartItems.some(item => item.id === productId);
  };

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by book name or author..."
          className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#c33c3d]/30 focus:border-[#c33c3d] transition-colors"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Category — separate, always visible */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-2.5">
        <h3 className="text-[11px] font-bold text-gray-400 tracking-wide mb-1.5">CATEGORY</h3>
        <div className="flex flex-wrap gap-1.5">
          {mainCategories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setSelectedMainCategory(category.id);
                setSelectedSubcategory('All');
              }}
              className={`px-3 py-1.5 rounded-md font-medium text-xs transition-all ${
                selectedMainCategory === category.id
                  ? 'bg-[#c33c3d] text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Subcategory + Price — collapsible dropdowns */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <FilterDropdown label="SUBCATEGORY" value={selectedSubcategory} isDefault={selectedSubcategory === 'All'}>
          {(close) => (
            <div className="flex flex-wrap gap-1.5">
              {subcategories.map((subcategory) => (
                <button
                  key={subcategory}
                  onClick={() => {
                    setSelectedSubcategory(subcategory);
                    close();
                  }}
                  className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    selectedSubcategory === subcategory
                      ? 'bg-[#c33c3d] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {subcategory}
                </button>
              ))}
            </div>
          )}
        </FilterDropdown>

        <FilterDropdown label="PRICE" value={selectedPrice} isDefault={selectedPrice === 'All Prices'}>
          {(close) => (
            <div className="flex flex-col gap-1">
              {priceRanges.map((range) => (
                <button
                  key={range}
                  onClick={() => {
                    setSelectedPrice(range);
                    close();
                  }}
                  className={`text-left px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    selectedPrice === range
                      ? 'bg-[#c33c3d] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
          )}
        </FilterDropdown>
      </div>

      {/* Products Count */}
      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-1.5">
        <p className="text-xs font-medium text-blue-900">
          <span className="font-bold">{filteredProducts.length}</span> products
          {selectedSubcategory !== 'All' && (
            <span> in <span className="font-bold">{selectedSubcategory}</span></span>
          )}
          {searchTerm && <span> matching "<span className="font-bold">{searchTerm}</span>"</span>}
        </p>
      </div>

      {/* Product Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3">
        {filteredProducts.length === 0 ? (
          <div className="col-span-full text-center py-10">
            <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-gray-600 mb-1">No products found</h3>
            <p className="text-sm text-gray-500">Try adjusting your filters</p>
          </div>
        ) : (
          filteredProducts.map((product) => (
            <div
              key={product.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex flex-col"
            >
              <div className="p-2.5 flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-1.5 gap-1">
                  <span className="px-1.5 py-0.5 bg-primary-50 text-navbar text-[10px] font-bold rounded-full border border-primary-200 truncate">
                    {product.badge}
                  </span>
                  <span className="px-1.5 py-0.5 bg-green-50 text-green-700 text-[10px] font-bold rounded-full shrink-0">
                    {product.discount}
                  </span>
                </div>

                <h3 className="text-sm font-bold text-gray-900 mb-1 line-clamp-2 min-h-[36px]">
                  {product.title}
                </h3>

                <p className="text-[11px] text-gray-500 mb-1.5 truncate">{product.author}</p>

                <div className="flex items-center gap-1 mb-1.5">
                  <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                  <span className="text-xs font-bold text-gray-900">{product.rating}</span>
                  <span className="text-[11px] text-gray-500">({product.reviews.toLocaleString()})</span>
                </div>

                <div className="flex flex-wrap gap-1 mt-auto">
                  {product.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              <div className="p-2.5 pt-0">
                <div className="flex items-baseline gap-1.5 mb-1.5">
                  <span className="text-lg font-bold text-gray-900">₹{product.price}</span>
                  <span className="text-xs text-gray-400 line-through">₹{product.originalPrice}</span>
                </div>
                <Button
                  variant={isInCart(product.id) || addedToCart[product.id] ? "primary" : "secondary"}
                  size="sm"
                  fullWidth
                  onClick={() => handleAddToCart(product)}
                  disabled={addedToCart[product.id]}
                >
                  {addedToCart[product.id] ? (
                    <span className="flex items-center justify-center gap-1.5">
                      <Check className="w-3.5 h-3.5" />
                      Added!
                    </span>
                  ) : isInCart(product.id) ? (
                    'In Cart'
                  ) : (
                    'Add to Cart'
                  )}
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Cart;